/**
 * FRONT-847: shared "tool signal" contract — frontend mirror.
 *
 * This file mirrors `src/agents/streams/tool-signals.ts` on the backend. The
 * literal signal-type strings and the `SIGNAL_PART_TYPE` prefix helper MUST
 * stay byte-identical between the two files: the backend emits SSE parts
 * named `data-<type>`, and this frontend copy is what `message-bubble.tsx`
 * uses to recognize and render them as cards (e.g. a "Connect this
 * integration" card). Keep both files dependency-free (pure constants +
 * types + one helper) — this file is React-free by design.
 */

/** The closed set of signal types a tool result may emit. */
export const TOOL_SIGNAL_TYPES = Object.freeze([
  "connect-integration",
  "enable-capability",
  "starter-cron-proposal",
  "quick-replies",
  "onboarding-recap",
  "continue-offer",
] as const);

export type ToolSignalType = (typeof TOOL_SIGNAL_TYPES)[number];

/** Payload for a `connect-integration` signal. */
export interface ConnectIntegrationSignalData {
  /** The provider's unique key (e.g. "slack"). */
  providerKey: string;
  /** The provider's display name (e.g. "Slack"). */
  providerName: string;
  /** Optional capability name the connection is needed for. */
  capability?: string;
  /** Whether the integration needs a fresh connection or a reconnection. */
  flavour: "connect" | "reconnect";
  /**
   * FRONT-978: whether there's a real blocked action behind this offer that
   * a one-tap Retry should re-send once connected. `true`/omitted (the
   * existing delegation-failure emitter's behavior) — the preceding user
   * message WAS an attempted task, so re-sending it after connecting makes
   * sense. Explicit `false` — this card was raised proactively
   * (`offer_connect_integration`) with no blocked task behind it; the
   * nearest preceding user message may be nothing more than "how do I
   * connect X", which Retry would just resend pointlessly. The card omits
   * Retry whenever this is `false`, regardless of `originalUserText`.
   */
  retryable?: boolean;
}

/** Payload for an `enable-capability` signal. */
export interface EnableCapabilitySignalData {
  /** The agent that needs the capability enabled. */
  agentId: string;
  /** The capability names to enable. */
  capabilities: string[];
}

/** One proposed starter automation inside a `starter-cron-proposal` signal. */
export interface StarterCronProposalItem {
  /** Display name of the proposed cron (e.g. "Morning briefing"). */
  name: string;
  /** Human-readable schedule (e.g. "Weekdays at 7:30"). */
  schedule: string;
  /** One-line description of what the cron does. */
  description: string;
}

/** Payload for a `starter-cron-proposal` signal (FRONT-907: the day-one
 * starter-automations card; seeded server-side, never produced by a tool). */
export interface StarterCronProposalSignalData {
  /** The proposed starter crons, in display order. */
  crons: StarterCronProposalItem[];
}

/** One tappable option inside a `quick-replies` signal. */
export interface QuickReplyOption {
  /** Button label shown to the user. */
  label: string;
  /** The user message the tap sends into the chat, verbatim. */
  send: string;
  /** Primary-styled button (the recommended action). */
  primary?: boolean;
  /**
   * 2026-07-21 (FRONT-907): when "call", tapping this option starts a live
   * voice call instead of sending `send` as a text message — the day-one
   * interview offer's "Let's talk" option. Omitted (the default) sends text.
   */
  action?: "call";
}

/** Payload for a `quick-replies` signal (FRONT-907: tappable follow-ups on
 * scripted messages so a seeded question is a button, not dead prose). */
export interface QuickRepliesSignalData {
  /** Stable key distinguishing multiple quick-reply cards in one session. */
  key: string;
  /** The tappable options, in display order. */
  options: QuickReplyOption[];
}

/** One captured fact inside an `onboarding-recap` signal's summary list. */
export interface OnboardingRecapItem {
  /** Human-readable label, e.g. "Key people" or "Must-dos". */
  label: string;
  /** Human-readable value for that field. */
  value: string;
  /** FRONT-2349: the Perfect Day field this row stages (e.g. "mustDos",
   *  "people"), so the recap can find the row's own proposal. Absent on
   *  recaps staged before it existed. */
  field?: string;
}

/** Payload for an `onboarding-recap` signal (2026-07-21: the day-one
 * interview's closing recap — what was captured, pending approval via the
 * reflect proposals this run created). */
export interface OnboardingRecapSignalData {
  /** The reflect run id these proposals belong to — approve-all/reject-all
   *  scope by this id (existing `/api/reflect/proposals/*-all` routes). */
  runId: number;
  /** What will be saved, for display — one line per captured field/person. */
  items: OnboardingRecapItem[];
}

/** Payload for a `continue-offer` signal (FRONT-2465, FRONT-2601: the
 * "Continue" button under a server-authored fallback reply). Emitted by the
 * chat proxy itself, never by a tool. Mirrors the backend type in
 * `src/agents/streams/tool-signals.ts`; both fields are optional because
 * messages persisted before FRONT-2601 carry neither. */
export interface ContinueOfferSignalData {
  /** Hide the boilerplate sentence and show the card's own label instead. */
  replacesText?: boolean;
  /** What the button sends; missing means "continue". */
  prompt?: string;
}

/** A typed signal a tool result may carry, keyed by its `type`. */
export type ToolSignal =
  | { type: "connect-integration"; data: ConnectIntegrationSignalData }
  | { type: "enable-capability"; data: EnableCapabilitySignalData }
  | { type: "starter-cron-proposal"; data: StarterCronProposalSignalData }
  | { type: "quick-replies"; data: QuickRepliesSignalData }
  | { type: "onboarding-recap"; data: OnboardingRecapSignalData }
  | { type: "continue-offer"; data: ContinueOfferSignalData };

/**
 * Maps a signal type to the SSE part-type string the chat proxy emits it
 * under (e.g. "connect-integration" -> "data-connect-integration").
 */
export function SIGNAL_PART_TYPE(type: ToolSignalType): string {
  return `data-${type}`;
}
